import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import ReactDOM from 'react-dom';

import { Add01Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { DEFAULT_TAG_COLOR, TAG_COLOR_PALETTE } from 'constants/tags';
import { useClickOutside } from 'hooks/click-outside';
import { useTranslation } from 'react-i18next';

import styles from './TagSearchDropdown.module.scss';

export const TagSearchDropdown = ({
  visible,
  tags = [],
  allTagsForNameCheck = null,
  recentTags = [],
  selectedIds = null,
  onSelect = () => {},
  onCreate,
  onClose = () => {},
  triggerRef = null,
  usePortal = false,
  searchPlaceholder,
  recentLabel,
  availableLabel,
  noResultsLabel,
}) => {
  const { t } = useTranslation();
  const multiselect = selectedIds !== null;
  const ref = useRef(null);
  const inputRef = useRef(null);
  const [search, setSearch] = useState('');
  // Start hidden — useLayoutEffect positions it before revealing, eliminating any visible shift
  const [portalStyle, setPortalStyle] = useState({ visibility: 'hidden' });

  useEffect(() => {
    if (!visible) setSearch('');
  }, [visible]);

  useClickOutside(ref, onClose, triggerRef);

  useLayoutEffect(() => {
    if (!usePortal || !triggerRef?.current) return;

    const positionFromTrigger = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return null;
      return {
        position: 'fixed',
        top: rect.bottom + 8,
        left: rect.left,
        width: 280,
        minWidth: 280,
        maxWidth: 280,
        zIndex: 9999,
      };
    };

    if (!visible) {
      setPortalStyle({ visibility: 'hidden' });
      return;
    }

    const initial = positionFromTrigger();
    if (!initial) return;
    setPortalStyle({ ...initial, visibility: 'visible' });

    const onScroll = _event => {
      const next = positionFromTrigger();
      if (next) setPortalStyle(prev => ({ ...prev, ...next }));
    };

    window.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true, passive: true });
  }, [visible, usePortal, triggerRef]);

  const normalizedSearch = search.trim().toLowerCase();
  const recentIds = new Set(recentTags.map(t => Number(t.id)));
  const filtered = tags.filter(tag => {
    if (!tag.name.toLowerCase().includes(normalizedSearch)) return false;
    // When not searching, exclude tags already shown in the "Recently used" section
    if (!normalizedSearch && recentIds.has(Number(tag.id))) return false;
    return true;
  });
  // Use allTagsForNameCheck when provided (e.g. full company tags) so we don't show
  // "Create" for an existing tag that's just not in `tags` (e.g. already assigned).
  const tagsToCheckNames = allTagsForNameCheck ?? tags;
  const exactMatch = tagsToCheckNames.some(tag => tag.name.toLowerCase() === normalizedSearch);
  const canCreateTag = typeof onCreate === 'function';
  const showCreate = canCreateTag && normalizedSearch.length > 0 && !exactMatch;

  // Same condition as the "Recently used" block below — when true, filtered is often empty (recent ids excluded).
  const recentSectionVisible = recentTags.length > 0 && !normalizedSearch;
  const showNoTagsMessage = filtered.length === 0 && !showCreate && !recentSectionVisible;

  const handleSelect = tag => {
    if (!multiselect) setSearch('');
    onSelect(tag.id);
  };

  const handleCreate = async () => {
    if (!normalizedSearch || !canCreateTag) return;
    // Only clear the input once creation succeeds, so a failed create
    // (duplicate name, missing permission, network error) keeps the typed
    // name available for retry instead of forcing the user to retype it.
    try {
      await onCreate({ name: search.trim(), color: DEFAULT_TAG_COLOR });
      setSearch('');
    } catch {
      // Call sites surface their own error feedback; keep the search intact.
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && showCreate) handleCreate();
    if (e.key === 'Escape') onClose();
  };

  if (!visible) return null;

  const content = (
    <div
      className={styles.root}
      ref={ref}
      style={usePortal ? portalStyle : undefined}
      onClick={e => e.stopPropagation()}>
      <div className={styles.inputWrapper}>
        <input
          ref={inputRef}
          autoFocus
          className={styles.input}
          placeholder={searchPlaceholder ?? t('tags.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          onClick={e => e.stopPropagation()}
        />
      </div>

      <div className={styles.listWrapper}>
        <div className={styles.list}>
          {recentSectionVisible && (
            <>
              <div className={styles.listHeader}>{recentLabel ?? t('tags.recentlyUsed')}</div>
              {recentTags.map(tag => {
                const isChecked = multiselect && selectedIds.includes(Number(tag.id));
                return (
                  <div
                    key={tag.id}
                    className={styles.tagRow}
                    onClick={e => {
                      e.stopPropagation();
                      handleSelect(tag);
                    }}>
                    {multiselect ? (
                      <span
                        className={`${styles.checkbox} ${isChecked ? styles.checkboxChecked : ''}`}
                      />
                    ) : null}
                    <span className={styles.dot} style={{ '--tag-color': tag.color }} />
                    <div className={styles.tagInfo}>
                      <span className={styles.tagName}>{tag.name}</span>
                      {tag.description && <span className={styles.tagDesc}>{tag.description}</span>}
                    </div>
                  </div>
                );
              })}
            </>
          )}
          {filtered.length > 0 && (
            <div className={styles.listHeader}>{availableLabel ?? t('tags.availableTags')}</div>
          )}
          {filtered.map(tag => {
            const isChecked = multiselect && selectedIds.includes(Number(tag.id));
            return (
              <div
                key={tag.id}
                className={styles.tagRow}
                onClick={e => {
                  e.stopPropagation();
                  handleSelect(tag);
                }}>
                {multiselect ? (
                  <span
                    className={`${styles.checkbox} ${isChecked ? styles.checkboxChecked : ''}`}
                  />
                ) : (
                  <span className={styles.dot} style={{ '--tag-color': tag.color }} />
                )}
                {multiselect && (
                  <span className={styles.dot} style={{ '--tag-color': tag.color }} />
                )}
                <div className={styles.tagInfo}>
                  <span className={styles.tagName}>{tag.name}</span>
                  {tag.description && <span className={styles.tagDesc}>{tag.description}</span>}
                </div>
              </div>
            );
          })}

          {showNoTagsMessage && (
            <div className={styles.empty}>{noResultsLabel ?? t('tags.noTagsFound')}</div>
          )}

          {showCreate && (
            <div
              className={styles.createRow}
              onClick={e => {
                e.stopPropagation();
                handleCreate();
              }}>
              <span className={styles.createIcon}>
                <HugeiconsIcon icon={Add01Icon} size="11" strokeWidth="2.5" />
              </span>
              <div className={styles.tagInfo}>
                <span className={styles.tagName}>
                  {t('tags.createLabel', { name: search.trim() })}
                </span>
                <span className={styles.tagDesc}>{t('tags.createHint')}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (usePortal) return ReactDOM.createPortal(content, document.body);
  return content;
};
